import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';

import { Icon, IconButton } from 'components';
import Avatar from 'components/Avatar';
import { useResponsive } from 'hooks/useResponsive';
import { useAuth } from 'lib/providers/AuthProvider';
import { useCommonList } from 'lib/contexts/common-list-context';

import TopTitleBar from '../TopTitleBar';

import styles from './BasicInfo.module.scss';
import { AddBasicInfoModal } from './AddBasicInfoModal.component';

export const BasicInfo = ({ className }: { className?: string }) => {
  const { candidate } = useAuth();

  const [t] = useTranslation('candidate.profile');
  const screens = useResponsive();

  const { languageOptions: languageTag } = useCommonList();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isBasicInfoFullyCompleted, setIsBasicInfoFullyCompleted] =
    useState<boolean>(false);

  const [languages, setLanguages] = useState<string[]>();
  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [bio, setBio] = useState<string>();
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [location, setLocation] = useState<string>();

  useEffect(() => {
    if (candidate) {
      const {
        first_name,
        last_name,
        bio,
        avatar_url,
        languages,
        location,
        email,
        linkedin_url,
        phone_number,
      } = candidate;

      setIsBasicInfoFullyCompleted(
        !!(
          first_name &&
          last_name &&
          bio &&
          avatar_url &&
          languages?.data.length &&
          location &&
          email &&
          linkedin_url &&
          phone_number
        )
      );
      setFirstName(first_name);
      setLastName(last_name);
      setEmail(email);
      setBio(bio);
      setAvatarUrl(avatar_url);
      setLocation(location);
      setLanguages(languages?.data?.map((item) => item.id));
    }
  }, [candidate]);

  const languageText = useMemo(() => {
    const labelsList = languageTag
      ?.filter((item) => {
        if (languages && languages.includes(item.id)) return item.label;
      })
      .map((item) => item.label);
    return labelsList?.join(', ');
  }, [languages, languageTag]);

  const openBasicInfoPopup = () => {
    setIsModalOpen(true);
  };

  return (
    <article className={className}>
      <TopTitleBar
        title={t('basic-info.title')}
        isInfoComplete={isBasicInfoFullyCompleted}
        iconName="icon_pencil-filled"
        onIconClick={openBasicInfoPopup}
      />

      <section className={styles.content}>
        <section className={styles.bio}>
          <section className={styles.left}>
            <section
              onClick={openBasicInfoPopup}
              className={styles['image-container']}
            >
              <Avatar
                icon={avatarUrl}
                name={firstName || email || ''}
                size={screens.sm ? '80' : '100'}
              />
              <IconButton
                iconName="icon_pencil-filled"
                size="xsmallplus"
                className={styles.pencil}
                onClick={openBasicInfoPopup}
              />
            </section>

            <section className={styles['bio-details']}>
              <section className={styles['name-bio']}>
                <p onClick={openBasicInfoPopup} className={styles.name}>
                  {firstName && lastName
                    ? `${firstName} ${lastName}`
                    : t('basic-info.add-your-name')}
                </p>
                {bio ? (
                  <p className={cn(styles.info, styles['info-available'])}>
                    {bio}
                  </p>
                ) : (
                  <p className={styles.info}>{t('basic-info.no-bio')}</p>
                )}
              </section>

              <section className={styles['location-details']}>
                <section className={styles.location}>
                  <Icon
                    iconName="icon_pin"
                    size="small"
                    className={styles.pin}
                  />
                  {location ? (
                    <p
                      className={cn(
                        styles['location-name'],
                        styles['location-available']
                      )}
                    >
                      {location}
                    </p>
                  ) : (
                    <p className={styles['location-name']}>
                      {t('basic-info.no-location')}
                    </p>
                  )}
                </section>
                <p className={styles.dot}>•</p>
                <p
                  className={styles['contact-info']}
                  onClick={openBasicInfoPopup}
                >
                  {t('basic-info.contact-info')}
                </p>
              </section>
            </section>
          </section>
          {!isBasicInfoFullyCompleted && !screens.sm_desktop_down && (
            <p className={styles['warning-right']} onClick={openBasicInfoPopup}>
              {t('basic-info.warning')}
            </p>
          )}
        </section>
        <section className={styles.languages}>
          <section className={styles['language-box']}>
            <p className={styles['language-title']}>
              {t('basic-info.languages')}
            </p>
            {languageText ? (
              <p className={styles['language-names']}>{languageText}</p>
            ) : (
              <p className={styles['language-names']}>
                {t('basic-info.no-language-added')}
              </p>
            )}
          </section>
        </section>
        {!isBasicInfoFullyCompleted && screens.sm_desktop_down && (
          <div className={styles['warning-down']}>
            <p onClick={openBasicInfoPopup} className={styles.text}>
              {t('basic-info.warning')}
            </p>
          </div>
        )}
      </section>
      {isModalOpen && (
        <AddBasicInfoModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </article>
  );
};

export default BasicInfo;
